// Shared look of the Organization Settings page: its cards, labels, inputs and
// dropdowns. Every card on the page uses these so they read as one page.

import { MOBILE_SX } from '../../../../mobile/mobileApp';

const ORANGE = '#F79420';

// Text fields, selects and multi-selects: a dark well with a thin border that
// turns orange with a soft ring on focus.
export const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: '#141414',
    borderRadius: '8px',
    minHeight: '38px',
    fontWeight: 400,
    transition: 'box-shadow 120ms ease',
    '& fieldset': { borderColor: '#2A2A2A', transition: 'border-color 120ms ease' },
    '&:hover fieldset': { borderColor: '#4A4A4A' },
    '&.Mui-focused fieldset': { borderColor: ORANGE, borderWidth: '1px' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(247,148,32,0.15)' },
    '&.Mui-disabled fieldset': { borderColor: '#222222' },
    '& input': { color: '#FDFDFD', fontSize: 14, fontFamily: 'Inter', fontWeight: 400, lineHeight: '20px' },
    '& input::placeholder': { color: '#6E6E6E', opacity: 1 }
  },
  '& .MuiAutocomplete-inputRoot': { py: '3px !important', pl: '6px !important', gap: '4px' },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { py: '4px !important' },
  // The app theme paints every Autocomplete tag light grey on near-white and
  // disabled inputs light grey, both with !important; keep this page dark.
  // (Set here, at the input, because the theme rule is more specific than a
  // chip's own sx.)
  '& .MuiAutocomplete-tag': {
    m: '0 !important',
    color: '#FDFDFD !important',
    fontSize: '12px !important',
    fontWeight: '500 !important'
  },
  '& .Mui-disabled': { background: 'transparent !important' },
  '& .MuiSvgIcon-root': { color: '#868686' },
  '& .MuiAutocomplete-popupIndicator': { color: '#868686' },
  '& .MuiAutocomplete-popupIndicatorOpen': { color: ORANGE }
};

// MUI Select (single choice) with the same well as inputSx.
export const selectSx = {
  background: '#141414',
  borderRadius: '8px',
  color: '#FDFDFD',
  fontSize: 14,
  fontFamily: 'Inter',
  fontWeight: 400,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2A2A2A' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4A4A4A' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE, borderWidth: '1px' },
  '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(247,148,32,0.15)' },
  '& .MuiSvgIcon-root': { color: '#868686' },
  '& .MuiSelect-select': { py: '10px', px: '12px' }
};

export const fieldLabelSx = {
  color: '#FDFDFD',
  fontSize: 14,
  fontFamily: 'Inter',
  fontWeight: 500,
  lineHeight: '20px'
};

// The title of a section inside a card (Department, Roles, an attribute list).
export const sectionTitleSx = {
  color: '#FDFDFD',
  fontSize: 15,
  fontFamily: 'Inter',
  fontWeight: 600,
  lineHeight: '20px'
};

// Small uppercase column heading above rows (DEPARTMENT · MANAGERS).
export const columnHeadingSx = {
  color: '#868686',
  fontSize: 11,
  fontFamily: 'Inter',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const
};

export const helperTextSx = {
  color: '#B6B6B6',
  fontSize: 12,
  fontFamily: 'Inter',
  fontWeight: 400,
  lineHeight: '16px'
};

export const cardSx = {
  background: '#1C1C1C',
  overflow: 'hidden',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column'
} as const;

export const cardHeaderSx = {
  // One height for every card header, with or without buttons in it.
  px: '25px',
  py: '9px',
  minHeight: '58px',
  boxSizing: 'border-box',
  borderBottom: '1px solid #444444',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  // A phone: the buttons drop under the title when they don't fit beside it.
  [MOBILE_SX]: { px: '16px', flexWrap: 'wrap', rowGap: '8px', py: '10px' }
};

export const cardTitleSx = {
  color: '#FDFDFD',
  fontSize: 16,
  fontFamily: 'Inter',
  fontWeight: 700
};

// Dropdown paper for Select / Autocomplete: a floating rounded panel with
// rounded options; the chosen ones tint orange and carry a check.
export const menuPaperSx = {
  mt: '6px',
  bgcolor: '#1C1C1C',
  backgroundImage: 'none',
  border: '1px solid #2A2A2A',
  borderRadius: '10px',
  boxShadow: '0 16px 40px rgba(0,0,0,0.55)',
  '& .MuiAutocomplete-listbox, & .MuiList-root': { p: '6px' },
  '& .MuiAutocomplete-option, & .MuiMenuItem-root': {
    color: '#FDFDFD !important',
    fontSize: '14px !important',
    fontFamily: 'Inter',
    borderRadius: '6px',
    minHeight: '36px',
    px: '10px',
    '&:hover, &.Mui-focused, &.Mui-focusVisible': { bgcolor: '#262626' },
    '&[aria-selected="true"], &.Mui-selected': { bgcolor: 'rgba(247,148,32,0.10)' },
    '&[aria-selected="true"].Mui-focused, &.Mui-selected:hover': { bgcolor: 'rgba(247,148,32,0.16)' },
    '&[aria-disabled="true"]': { opacity: 0.5 }
  },
  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': { color: '#868686', fontSize: 13, fontFamily: 'Inter' }
};

// A chosen value inside a multi-select (a department's manager, a list value):
// the DS "options" pill (fill #565656, text #FDFDFD, border #0F0F0F). The
// !importants beat the app theme's Autocomplete tag override.
export const valueChipSx = {
  height: '24px !important',
  borderRadius: '4px',
  background: '#565656 !important',
  border: '1px solid #0F0F0F !important',
  color: '#FDFDFD !important',
  fontSize: '12px !important',
  fontFamily: 'Inter',
  fontWeight: '500 !important',
  '& .MuiChip-label': { px: '8px !important', py: '0 !important' },
  '& .MuiChip-deleteIcon': {
    color: 'rgba(253,253,253,0.7) !important',
    fontSize: '15px !important',
    mr: '4px',
    '&:hover': { color: '#FDFDFD !important' }
  }
};

// A read-only chip (a role from Capabilities, an org admin): outlined, dimmer.
export const lockedChipSx = {
  ...valueChipSx,
  background: 'transparent !important',
  border: '1px solid #444444 !important',
  color: '#B6B6B6 !important',
  '& .MuiChip-icon': { color: '#868686', fontSize: 13, ml: '7px', mr: '-3px' }
};

// An invited manager who hasn't signed up yet.
export const pendingChipSx = {
  ...valueChipSx,
  background: 'transparent !important',
  border: '1px dashed #868686 !important',
  color: '#B6B6B6 !important'
};

// An orange inline text action ("Invite new manager").
export const textActionSx = {
  border: 'none',
  bgcolor: 'transparent',
  p: 0,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'Inter',
  fontWeight: 600,
  color: ORANGE,
  '&:hover': { textDecoration: 'underline' },
  '&:disabled': { color: '#565656', cursor: 'default', textDecoration: 'none' }
};

// A compact dashed "add" button, left-aligned ("+ Add department", "+ Create new
// attribute list").
export const addRowButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  border: '1px dashed #444444',
  borderRadius: '6px',
  bgcolor: 'transparent',
  px: '14px',
  py: '8px',
  cursor: 'pointer',
  color: ORANGE,
  fontSize: 13,
  fontFamily: 'Inter',
  fontWeight: 600,
  transition: 'background-color 120ms ease, border-color 120ms ease',
  '&:hover': { bgcolor: 'rgba(247,148,32,0.06)', borderColor: ORANGE }
};

// Width of the name column in Department / Roles rows.
export const NAME_COLUMN = 220;
